import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Webhook, Plus, Pause, Play, Trash2, ExternalLink, AlertTriangle, RefreshCw, BookOpen, Copy } from "lucide-react";
import { webhooksApi, formatApiError } from "../../lib/ledgerApi";
import { toast } from "sonner";

const STATUS_COLOR = {
  active: "border-status-ok/40 bg-status-ok/10 text-status-ok",
  paused: "border-amber-500/40 bg-amber-500/10 text-amber-600 dark:text-amber-400",
  disabled_by_system: "border-status-tampered/40 bg-status-tampered/10 text-status-tampered",
};

function StatusPill({ status, "data-testid": testid }) {
  return (
    <span
      data-testid={testid}
      className={`px-2 py-0.5 border rounded-sm text-[10px] uppercase tracking-widest ${STATUS_COLOR[status] || "border-border bg-muted/30 text-muted-foreground"}`}
    >
      {status?.replace(/_/g, " ") || "—"}
    </span>
  );
}

function CreateWizard({ open, onClose, onCreated, eventTypes }) {
  const [step, setStep] = useState(1);
  const [url, setUrl] = useState("");
  const [description, setDescription] = useState("");
  const [selected, setSelected] = useState(new Set());
  const [secret, setSecret] = useState(null);
  const [createdId, setCreatedId] = useState(null);
  const [busy, setBusy] = useState(false);

  const grouped = useMemo(() => {
    const map = {};
    (eventTypes || []).forEach((e) => {
      if (e.event_type === "webhook.test") return;
      (map[e.category] = map[e.category] || []).push(e.event_type);
    });
    return map;
  }, [eventTypes]);

  const reset = () => {
    setStep(1); setUrl(""); setDescription(""); setSelected(new Set());
    setSecret(null); setCreatedId(null); setBusy(false);
  };

  if (!open) return null;

  const toggle = (et) => {
    const next = new Set(selected);
    if (next.has(et)) next.delete(et);
    else next.add(et);
    setSelected(next);
  };

  const toggleAll = () => {
    if (selected.has("*")) setSelected(new Set());
    else setSelected(new Set(["*"]));
  };

  const validateUrl = (u) => {
    if (!u) return false;
    return /^(https?:\/\/|__dev_receiver__:)/i.test(u);
  };

  const onCreate = async () => {
    setBusy(true);
    try {
      const event_types = selected.has("*") ? ["*"] : Array.from(selected);
      if (!event_types.length) {
        toast.error("Select at least one event type");
        setBusy(false); return;
      }
      const r = await webhooksApi.create({ url, description, event_types });
      setSecret(r.secret);
      setCreatedId(r.subscription.id);
      setStep(3);
      onCreated?.(r.subscription);
    } catch (e) {
      toast.error("Create failed", { description: formatApiError(e.response?.data?.detail) });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" data-testid="webhook-wizard">
      <div className="w-full max-w-2xl bg-card border border-border rounded-sm">
        <div className="flex items-center justify-between p-4 border-b border-border">
          <div className="flex items-center gap-2">
            <Webhook className="w-4 h-4" strokeWidth={1.5} />
            <h3 className="text-sm tracking-tight">New webhook</h3>
            <span className="text-[10px] uppercase tracking-widest text-muted-foreground ml-2">Step {step} / 3</span>
          </div>
          <button data-testid="wizard-close" onClick={() => { reset(); onClose(); }} className="text-muted-foreground hover:text-foreground text-sm">×</button>
        </div>

        {step === 1 && (
          <div className="p-6 space-y-5">
            <div>
              <label className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground mb-2 block">Endpoint URL</label>
              <input
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                placeholder="https://api.yourbusiness.com/webhooks/neopay"
                data-testid="wizard-url"
                className="w-full bg-background border border-border rounded-sm px-3 py-2 text-sm font-mono focus:outline-none focus:border-foreground"
              />
              <p className="text-[11px] text-muted-foreground mt-1.5">
                Must be HTTPS in production. NeoPay POSTs JSON with HMAC-SHA256 signature.
              </p>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground mb-2 block">Description (optional)</label>
              <input
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="ERP sync — Acme finance"
                data-testid="wizard-description"
                className="w-full bg-background border border-border rounded-sm px-3 py-2 text-sm focus:outline-none focus:border-foreground"
              />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <button
                disabled={!validateUrl(url)}
                onClick={() => setStep(2)}
                data-testid="wizard-next-1"
                className="px-4 py-2 border border-foreground bg-foreground text-background text-xs uppercase tracking-widest disabled:opacity-40"
              >
                Next: events
              </button>
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="p-6 space-y-4 max-h-[70vh] overflow-y-auto">
            <div className="flex items-center justify-between">
              <span className="text-[11px] uppercase tracking-widest text-muted-foreground">Choose events</span>
              <button onClick={toggleAll} className="text-[11px] underline" data-testid="wizard-toggle-all">
                {selected.has("*") ? "Clear all" : "Subscribe to all events"}
              </button>
            </div>
            {selected.has("*") ? (
              <div className="text-xs text-muted-foreground border border-dashed border-border p-4 rounded-sm">
                You'll receive every event NeoPay emits, including future ones. Use only for backend ETL pipelines.
              </div>
            ) : (
              <div className="space-y-4">
                {Object.entries(grouped).map(([cat, evs]) => (
                  <div key={cat}>
                    <div className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground mb-2">{cat}</div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      {evs.map((et) => (
                        <label key={et} className="flex items-center gap-2 text-xs cursor-pointer p-1.5 hover:bg-muted/30 rounded-sm">
                          <input
                            type="checkbox"
                            checked={selected.has(et)}
                            onChange={() => toggle(et)}
                            data-testid={`wizard-event-${et}`}
                          />
                          <span className="font-mono">{et}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
            <div className="flex justify-between pt-2">
              <button onClick={() => setStep(1)} className="text-xs text-muted-foreground hover:text-foreground">← Back</button>
              <button
                disabled={selected.size === 0 || busy}
                onClick={onCreate}
                data-testid="wizard-create"
                className="px-4 py-2 border border-foreground bg-foreground text-background text-xs uppercase tracking-widest disabled:opacity-40"
              >
                {busy ? "Creating…" : "Create webhook"}
              </button>
            </div>
          </div>
        )}

        {step === 3 && secret && (
          <div className="p-6 space-y-5">
            <div className="border border-amber-500/40 bg-amber-500/10 text-amber-600 dark:text-amber-300 p-4 rounded-sm">
              <div className="flex items-center gap-2 mb-2 font-medium text-sm">
                <AlertTriangle className="w-4 h-4" strokeWidth={1.5} /> Save this secret now
              </div>
              <p className="text-xs leading-relaxed">
                This is the only time we'll show the signing secret. NeoPay uses it to sign every webhook delivery
                as <code className="font-mono">X-NeoPay-Signature: t=&lt;ts&gt;,v1=&lt;hex&gt;</code>. Store it as a server-side env var.
              </p>
            </div>
            <div className="border border-border rounded-sm p-3 font-mono text-xs break-all bg-background" data-testid="wizard-secret">
              {secret}
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => { navigator.clipboard.writeText(secret); toast.success("Secret copied"); }}
                data-testid="wizard-copy-secret"
                className="text-xs border border-border px-3 py-2 rounded-sm hover:border-foreground inline-flex items-center gap-1.5"
              >
                <Copy className="w-3 h-3" strokeWidth={1.5} /> Copy
              </button>
              <a
                href={`data:text/plain;charset=utf-8,NEOPAY_WEBHOOK_SECRET=${encodeURIComponent(secret)}`}
                download="neopay-webhook.env"
                data-testid="wizard-download-secret"
                className="text-xs border border-border px-3 py-2 rounded-sm hover:border-foreground"
              >
                Download as .env
              </a>
            </div>
            <div className="flex justify-end pt-2">
              <Link
                to={`/app/webhooks/${createdId}`}
                onClick={() => { reset(); onClose(); }}
                data-testid="wizard-done"
                className="px-4 py-2 border border-foreground bg-foreground text-background text-xs uppercase tracking-widest"
              >
                Done — open detail
              </Link>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default function Webhooks() {
  const [items, setItems] = useState([]);
  const [eventTypes, setEventTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [wizard, setWizard] = useState(false);

  const load = async () => {
    try {
      const r = await webhooksApi.list();
      setItems(r.subscriptions || []);
    } catch (e) {
      toast.error("Failed to load webhooks", { description: formatApiError(e.response?.data?.detail) });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    webhooksApi.eventTypes().then((r) => setEventTypes(r.event_types || [])).catch(() => {});
  }, []);

  const togglePause = async (s) => {
    try {
      await webhooksApi.update(s.id, { status: s.status === "active" ? "paused" : "active" });
      toast.success(s.status === "active" ? "Paused" : "Resumed");
      load();
    } catch (e) {
      toast.error("Update failed", { description: formatApiError(e.response?.data?.detail) });
    }
  };

  const remove = async (s) => {
    if (!window.confirm(`Delete webhook to ${s.url}?`)) return;
    try {
      await webhooksApi.remove(s.id);
      toast.success("Deleted");
      load();
    } catch (e) {
      toast.error("Delete failed", { description: formatApiError(e.response?.data?.detail) });
    }
  };

  return (
    <div className="max-w-7xl px-6 md:px-10 py-10 md:py-14" data-testid="webhooks-page">
      <div className="flex items-end justify-between mb-8 gap-4 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground mb-2">/ Developer</div>
          <h1 className="text-2xl sm:text-3xl tracking-tight font-medium">Webhooks</h1>
          <p className="text-sm text-muted-foreground mt-2 max-w-2xl">
            Subscribe to events NeoPay emits across the ledger. HMAC-signed, retried on failure with exponential backoff,
            and tamper-evident via included <code className="font-mono">proof_url</code>.
          </p>
        </div>
        <div className="flex gap-2">
          <a
            href="/api/webhooks/spec/markdown"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="webhooks-spec-link"
            className="text-xs border border-border px-3 py-2 rounded-sm hover:border-foreground inline-flex items-center gap-1.5"
          >
            <BookOpen className="w-3.5 h-3.5" strokeWidth={1.5} /> Integration spec
          </a>
          <button
            onClick={() => setWizard(true)}
            data-testid="webhooks-create-btn"
            className="text-xs border border-foreground bg-foreground text-background px-4 py-2 rounded-sm uppercase tracking-widest inline-flex items-center gap-1.5"
          >
            <Plus className="w-3.5 h-3.5" strokeWidth={1.5} /> New webhook
          </button>
        </div>
      </div>

      {loading && <div className="text-sm text-muted-foreground">Loading…</div>}

      {!loading && items.length === 0 && (
        <div className="border border-dashed border-border rounded-sm p-12 text-center" data-testid="webhooks-empty">
          <Webhook className="w-8 h-8 mx-auto mb-3 text-muted-foreground" strokeWidth={1.5} />
          <h3 className="text-base mb-2">No webhooks yet</h3>
          <p className="text-xs text-muted-foreground max-w-md mx-auto mb-5">
            Get notified the moment an invoice is paid, a transfer settles, or an agent payment is blocked.
            Subscribe to as many or as few event types as you need.
          </p>
          <button
            onClick={() => setWizard(true)}
            className="text-xs border border-foreground bg-foreground text-background px-4 py-2 rounded-sm uppercase tracking-widest"
            data-testid="webhooks-empty-cta"
          >
            Create your first webhook
          </button>
        </div>
      )}

      {!loading && items.length > 0 && (
        <div className="border border-border rounded-sm overflow-x-auto">
          <table className="w-full text-sm" data-testid="webhooks-table">
            <thead className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground border-b border-border">
              <tr>
                <th className="text-left p-3 font-normal">URL</th>
                <th className="text-left p-3 font-normal">Events</th>
                <th className="text-left p-3 font-normal">Status</th>
                <th className="text-left p-3 font-normal">Last delivery</th>
                <th className="text-left p-3 font-normal">Failures</th>
                <th className="text-right p-3 font-normal">Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.map((s) => (
                <tr key={s.id} className="border-t border-border hover:bg-muted/20" data-testid={`webhook-row-${s.id}`}>
                  <td className="p-3 font-mono text-xs max-w-xs truncate">
                    <Link to={`/app/webhooks/${s.id}`} className="hover:underline">{s.url}</Link>
                    {s.description && <div className="text-[11px] text-muted-foreground truncate font-sans">{s.description}</div>}
                  </td>
                  <td className="p-3 text-[11px] font-mono text-muted-foreground max-w-[200px] truncate">
                    {s.event_types?.includes("*") ? "all events" : (s.event_types || []).join(", ")}
                  </td>
                  <td className="p-3"><StatusPill status={s.status} data-testid={`webhook-status-${s.id}`} /></td>
                  <td className="p-3 text-[11px] text-muted-foreground">
                    {s.last_delivery_at ? new Date(s.last_delivery_at).toLocaleString() : "—"}
                  </td>
                  <td className="p-3 text-[11px]">
                    {(s.consecutive_failure_count || 0) > 0 ? (
                      <span className="text-amber-600 dark:text-amber-400">{s.consecutive_failure_count}</span>
                    ) : <span className="text-muted-foreground">0</span>}
                  </td>
                  <td className="p-3 text-right">
                    <div className="inline-flex gap-1">
                      <Link to={`/app/webhooks/${s.id}`} className="p-1.5 border border-border rounded-sm hover:border-foreground" data-testid={`webhook-view-${s.id}`}>
                        <ExternalLink className="w-3 h-3" strokeWidth={1.5} />
                      </Link>
                      <button onClick={() => togglePause(s)} className="p-1.5 border border-border rounded-sm hover:border-foreground" data-testid={`webhook-toggle-${s.id}`}>
                        {s.status === "active" ? <Pause className="w-3 h-3" strokeWidth={1.5} /> : <Play className="w-3 h-3" strokeWidth={1.5} />}
                      </button>
                      <button onClick={() => remove(s)} className="p-1.5 border border-border rounded-sm hover:border-status-tampered text-muted-foreground hover:text-status-tampered" data-testid={`webhook-delete-${s.id}`}>
                        <Trash2 className="w-3 h-3" strokeWidth={1.5} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <CreateWizard
        open={wizard}
        onClose={() => setWizard(false)}
        onCreated={() => load()}
        eventTypes={eventTypes}
      />
    </div>
  );
}
